import { Box } from '@chakra-ui/react';
import { CTAButton } from '@gamma/ui/buttons';
import { IPasswordResetForm, PasswordResetForm } from '@gamma/ui/forms';
import { ErrorBoundary } from '@gamma/ui/overlay';
import {
  CardPageBody,
  CardPageCTA,
  CardPageHeading,
  CardPageIllustration,
  CardPageSubtitle,
} from '@gamma/ui/pages';
import { GraphQLReqStatus, LoadingSpinner } from '@gamma/ui/progress';
import { graphqlErrorRedirects } from '@investigator/shared/constants';
import { i18n } from '@investigator/shared/localization';
import { AuthContext } from '@investigator/shared/providers';
import { Auth } from 'aws-amplify';
import { useContext, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router';

import {
  useAcceptPendingTerms,
  useLogOperationCall,
  useUserSession,
} from '@investigator/shared/hooks';
import { useActivateUserAccount } from '../../../queries';
import { IRegistrationContext } from '../Registration';

export const SetPassword = () => {
  const { signOut } = useUserSession();
  const navigate = useNavigate();
  const { user: userContext, setRouteAccessAllowed } =
    useOutletContext<IRegistrationContext>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const logOperationCall = useLogOperationCall();
  const { setUser } = useContext(AuthContext);

  // Use the hook for proper EULA acceptance with deduplication
  const { acceptEulaNow } = useAcceptPendingTerms();

  const [
    activateUserAccount,
    { loading: activateUserLoading, error: activateUserError },
  ] = useActivateUserAccount();

  const handlePasswordResetSubmission = async ({
    newPassword,
  }: IPasswordResetForm) => {
    try {
      setIsLoading(true);
      const user = await Auth.signIn(
        userContext.username,
        userContext.password,
      );

      if (user.challengeName === 'NEW_PASSWORD_REQUIRED') {
        await Auth.completeNewPassword(user, newPassword);

        try {
          await logOperationCall('NEW_PASSWORD_UPDATED', 'USER');
        } catch (e) {
          console.error('[SetPassword] Failed to log password update:', e);
        }

        // Use the hook for EULA acceptance instead of direct call
        try {
          const eulaResult = await acceptEulaNow();
          if (eulaResult.success) {
            await logOperationCall('PRIVACY_POLICY_ACKNOWLEDGED', 'USER');
          } else if (eulaResult.error) {
            console.error(
              '[SetPassword] EULA acceptance failed:',
              eulaResult.error,
            );
            // Continue anyway - don't block user registration
          }
        } catch (e) {
          console.error('[SetPassword] Error accepting EULA:', e);
          // Continue anyway - don't block user registration
        }

        try {
          await activateUserAccount();
        } catch (e) {
          console.error('[SetPassword] Failed to activate user account:', e);
          // Continue anyway - activation is not critical
        }

        const currentUser = await Auth.currentAuthenticatedUser();
        setUser?.(currentUser);

        if (currentUser?.attributes['custom:allow_no_mfa']) {
          navigate('../../');
        } else {
          setRouteAccessAllowed(true);
          navigate('../mfa', { replace: true });
        }
      }
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
      console.error('[SetPassword] Password reset error:', e);
      // Could add user-facing error message here
    }
  };

  const { title, subtitle, cta } = i18n.pages.registration.setPassword;

  return (
    <ErrorBoundary page="Registration Set Password Page">
      <Box data-testid="set-password-card">
        <CardPageHeading>{title}</CardPageHeading>
        <CardPageIllustration illustration="LockedShield" />
        <CardPageSubtitle>{subtitle}</CardPageSubtitle>
        <CardPageBody>
          {isLoading ? (
            <LoadingSpinner />
          ) : (
            <PasswordResetForm
              onSubmit={[handlePasswordResetSubmission]}
              newPasswordContent={{
                placeholder: i18n.forms.labels.newPassword,
                label: i18n.forms.labels.newPassword,
              }}
              confirmPasswordContent={{
                placeholder: i18n.forms.labels.confirmPassword,
                label: i18n.forms.labels.confirmPassword,
              }}
              autoFocusField="newPassword"
            />
          )}
        </CardPageBody>
      </Box>
      <CardPageCTA>
        <CTAButton type="submit" form="passwordResetForm" isLoading={isLoading}>
          {cta}
        </CTAButton>
      </CardPageCTA>
      {(activateUserLoading || activateUserError) && (
        <GraphQLReqStatus
          loading={activateUserLoading}
          error={activateUserError}
          isBackground={true}
          signOut={signOut}
          extended={graphqlErrorRedirects}
        />
      )}
    </ErrorBoundary>
  );
};
